// frontend/src/hooks/useUnreadChat.js
//
// Polls GET /api/v1/chat/unread-count every ~15s and exposes the current
// unread total, so the Dashboard nav can show a badge and the browser tab
// title can carry a "(3)" prefix. Also drives an optional desktop
// notification when the count RISES while the tab is backgrounded.
//
// SHORT-POLLING model (this Worker has no WebSockets) — see
// worker/lib/chat.js's header. 15s is a deliberate compromise: responsive
// enough for an internal tool, cheap enough to leave running on every
// dashboard page.
//
// The `active` argument lets a caller pause polling (e.g. while the Chat
// tab itself is open and doing its own faster polling, or when the module
// isn't visible to this role at all).
import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../api/client';

const POLL_MS = 15000;
const BASE_TITLE = 'TALIT PMS';

// Ask for desktop-notification permission once, lazily. Returns a promise
// that resolves to the permission string. Safe to call when the API is
// unavailable (older browsers, insecure context) — resolves to 'denied'.
export function ensureNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return Promise.resolve('denied');
  }
  if (Notification.permission !== 'default') {
    return Promise.resolve(Notification.permission);
  }
  return Notification.requestPermission().catch(() => 'denied');
}

export function useUnreadChat({ active = true, notify = false } = {}) {
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(0);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await apiFetch('/chat/unread-count');
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (!data || !data.success) return;
      const next = Number(data.unread_count) || 0;

      // Desktop toast when the count rises and the tab is hidden.
      if (
        notify
        && next > prevRef.current
        && typeof document !== 'undefined'
        && document.hidden
        && 'Notification' in window
        && Notification.permission === 'granted'
      ) {
        try {
          const n = new Notification('New message in TALIT PMS chat', {
            body: `You have ${next} unread message${next === 1 ? '' : 's'}.`,
            tag: 'talit-chat-unread', // collapses repeats into one toast
          });
          n.onclick = () => {
            window.focus();
            window.location.hash = 'chat';
            n.close();
          };
        } catch {
          /* Notification construction can throw on some platforms — ignore */
        }
      }

      prevRef.current = next;
      setUnread(next);
    } catch {
      /* network blip — keep the last known count */
    }
  }, [notify]);

  useEffect(() => {
    if (!active) return undefined;
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, poll]);

  // Reflect the count in the browser tab title, and restore it on unmount /
  // when the count returns to zero.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.title = unread > 0 ? `(${unread}) ${BASE_TITLE}` : BASE_TITLE;
    return () => { document.title = BASE_TITLE; };
  }, [unread]);

  // Let a caller force an immediate refresh (e.g. right after opening a
  // conversation and marking it read).
  return { unread, refresh: poll };
}
